
import { useState, useEffect } from "react"
import { Disclosure } from '@headlessui/react'
import { ChevronDown } from 'lucide-react'
import type { CostCodeBudget } from "../hooks/types"

interface CostCodeBudgetTableProps {
  costCodeBudgets: CostCodeBudget[]
  projectBudgetHours: number
  onSave: (budgets: CostCodeBudget[]) => void
  isLoading: boolean
}

export default function CostCodeBudgetTable({ costCodeBudgets, projectBudgetHours, onSave, isLoading }: CostCodeBudgetTableProps) {
  const [budgets, setBudgets] = useState<CostCodeBudget[]>([])
  const [totalHours, setTotalHours] = useState<number>(0)
  // Remaining hours to allocate
  const [remainingHours, setRemainingHours] = useState<number>(0)
  const [hasChanges, setHasChanges] = useState<boolean>(false)
  // No local expandedCategories state: Disclosure manages open/close per category

  // Initialize budgets when props change
  useEffect(() => {
    setBudgets(costCodeBudgets)
  }, [costCodeBudgets])

  // Calculate total hours and remaining hours whenever budgets or projectBudgetHours change
  useEffect(() => {
    const total = budgets.reduce((sum, budget) => sum + (budget.budgetedHours || 0), 0)
    setTotalHours(total)
    setRemainingHours(projectBudgetHours - total)
  }, [budgets, projectBudgetHours])

  // Group cost codes by category
  const groupedBudgets = budgets.reduce<Record<string, CostCodeBudget[]>>((acc, budget) => {
    const category = budget.costCodeId.category || "Uncategorized"
    if (!acc[category]) {
      acc[category] = []
    }
    acc[category].push(budget)
    return acc
  }, {})

  // Handle budget hour change
  const handleBudgetChange = (index: number, hours: number) => {
    const newBudgets = [...budgets]
    newBudgets[index].budgetedHours = hours
    setBudgets(newBudgets)
    setHasChanges(true)
  }


  // Handle save
  const handleSave = () => {
    onSave(budgets)
    setHasChanges(false)
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Cost Code Budgets</h3>
        <div className="flex space-x-6 text-sm text-gray-700">
          <div>
            Project Total: <span className="font-medium">{projectBudgetHours.toLocaleString()}</span>
          </div>
          <div>
            Allocated: <span className="font-medium">{totalHours.toLocaleString()}</span>
          </div>
          <div>
            Remaining: <span className="font-medium">{remainingHours.toLocaleString()}</span>
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {Object.entries(groupedBudgets).map(([category, categoryBudgets]) => {
          const sum = categoryBudgets.reduce((total, b) => total + (b.budgetedHours || 0), 0)
          return (
            <Disclosure key={category}>
              {({ open }) => (
                <div className="border rounded-md overflow-hidden">
                  <Disclosure.Button className="w-full bg-gray-100 px-4 py-2 flex justify-between items-center hover:bg-gray-200">
                    <span className="text-gray-800 font-semibold">{category}</span>
                    <div className="flex items-center">
                      <span className="text-gray-600 mr-2">{sum.toLocaleString()} hours</span>
                      <ChevronDown className={`h-5 w-5 transform ${open ? 'rotate-180' : ''} text-gray-600`} />
                    </div>
                  </Disclosure.Button>
                  <Disclosure.Panel>
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-200 text-gray-700">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Code</th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider">Description</th>
                            <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider">Budget Hours</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {categoryBudgets.map((budget) => {
                            const globalIndex = budgets.findIndex((b) => b.costCodeId._id === budget.costCodeId._id)
                            return (
                              <tr key={budget.costCodeId._id}>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{budget.costCodeId.code}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{budget.costCodeId.description}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                                  <input
                                    type="number"
                                    value={budget.budgetedHours || ''}
                                    onChange={(e) => {
                                      const value = e.target.value ? parseFloat(e.target.value) : 0
                                      handleBudgetChange(globalIndex, value)
                                    }}
                                    className="w-24 text-right border border-gray-300 p-1 rounded text-gray-900"
                                    disabled={isLoading}
                                  />
                                </td>
                              </tr>
                            )
                          })}
                        </tbody>
                      </table>
                    </div>
                  </Disclosure.Panel>
                </div>
              )}
            </Disclosure>
          )
        })}
      </div>

      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={isLoading || !hasChanges}
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {isLoading ? (
            <span className="flex items-center">
              <svg
                className="mr-2 h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              Saving...
            </span>
          ) : (
            "Save Budgets"
          )}
        </button>
      </div>
    </div>
  )
}
